/*
 * Copyright 2025 Kridaprema
 */
package com.karyadina.ui.theme

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.LibraryBooks
import androidx.compose.material.icons.automirrored.filled.TrendingUp
import androidx.compose.ui.graphics.vector.ImageVector

/**
 * Centralized flashcard icon system to eliminate hardcoded emoji and inconsistent icons.
 * All icons are Material Icons for a professional, cohesive look.
 */
object FlashcardIcons {

    // ============================================================================
    // MASTERY STATE ICONS
    // ============================================================================

    /**
     * Get icon for a mastery level.
     */
    fun getMasteryIcon(masteryLevel: String?): ImageVector {
        return when (masteryLevel?.uppercase()) {
            "NEW" -> Icons.Filled.FiberNew         // New/unfamiliar
            "LEARNING" -> Icons.Filled.School      // In progress
            "REVIEW" -> Icons.Filled.PlayCircle    // Review scheduled
            "MASTERED" -> Icons.Filled.CheckCircle // Complete
            "FAMILIAR" -> Icons.Filled.Visibility  // Partially known
            "KNOWN" -> Icons.Filled.VerifiedUser   // Well-known
            else -> Icons.Filled.HelpOutline
        }
    }

    /**
     * Get outlined icon for a mastery level (for badges, chips).
     */
    fun getMasteryIconOutlined(masteryLevel: String?): ImageVector {
        return when (masteryLevel?.uppercase()) {
            "NEW" -> Icons.Outlined.FiberManualRecord
            "LEARNING" -> Icons.Filled.School
            "REVIEW" -> Icons.Filled.PlayCircle
            "MASTERED" -> Icons.Filled.CheckCircle
            "FAMILIAR" -> Icons.Outlined.Visibility
            "KNOWN" -> Icons.Filled.VerifiedUser
            else -> Icons.Outlined.HelpOutline
        }
    }

    // ============================================================================
    // ACTION ICONS
    // ============================================================================

    val StarFilled = Icons.Filled.Star
    val StarOutlined = Icons.Outlined.StarOutline
    val DeleteOutlined = Icons.Outlined.Delete
    val EditOutlined = Icons.Outlined.Edit
    val CloseIcon = Icons.Filled.Close
    val MoreVertIcon = Icons.Filled.MoreVert
    val MoreHorizIcon = Icons.Filled.MoreHoriz

    // ============================================================================
    // STATUS & ALERT ICONS
    // ============================================================================

    val ScheduleIcon = Icons.Filled.Schedule          // Next review date
    val NotificationIcon = Icons.Filled.Notifications // Due for review
    val WarningIcon = Icons.Filled.Warning            // Alert/warning
    val NotificationImportantIcon = Icons.Filled.NotificationImportant // Urgent review
    val CheckIcon = Icons.Filled.Check                // Correct answer
    val CloseCircleIcon = Icons.Filled.Close          // Incorrect answer
    val CelebrationIcon = Icons.Filled.EmojiEvents    // Celebration/achievement

    // ============================================================================
    // HINT & SPECIAL ICONS
    // ============================================================================

    val HintIcon = Icons.Filled.LiveHelp              // Hint (help icon)
    val FormulaIcon = Icons.Filled.Functions          // Formula (replaces 📐)
    val AutoStoriesIcon = Icons.Filled.AutoStories    // How it works (replaces 📚)
    val BookIcon = Icons.AutoMirrored.Filled.LibraryBooks          // Reference/book
    val ImageIcon = Icons.Filled.Image                // Image attached
    val ViewIcon = Icons.Filled.Visibility            // View/expand
    val FullscreenIcon = Icons.Filled.Fullscreen      // Fullscreen image
    val DownloadIcon = Icons.Filled.Download          // Download/export

    // ============================================================================
    // STATISTICS & PROGRESS ICONS
    // ============================================================================

    val TrendingUpIcon = Icons.AutoMirrored.Filled.TrendingUp  // Progress/accuracy
    val BarChartIcon = Icons.Filled.BarChart          // Statistics
    val PieChartIcon = Icons.Filled.PieChart          // Distribution
    val AssignmentIcon = Icons.Filled.Assignment      // Tasks/subjects
    val TimelineIcon = Icons.Filled.Timeline          // History

    // ============================================================================
    // SCREEN & NAVIGATION ICONS
    // ============================================================================

    val BackIcon = Icons.AutoMirrored.Filled.ArrowBack  // Back button
    val AddIcon = Icons.Filled.Add                      // Add new
    val RefreshIcon = Icons.Filled.Refresh              // Refresh/reload
    val SearchIcon = Icons.Filled.Search                // Search

    // ============================================================================
    // TAG CATEGORY DETECTION ICONS
    // ============================================================================

    /**
     * Get icon for a tag based on its name/type.
     * Used to visually distinguish different tag categories.
     */
    fun getTagIcon(tag: String): String {
        val lowerTag = tag.lowercase()
        return when {
            lowerTag in listOf("formula", "equation", "math", "theorem", "proof") -> "📐"
            lowerTag in listOf("important", "critical", "key") -> "⭐"
            lowerTag in listOf("physics", "chemistry", "biology") -> "🔬"
            lowerTag in listOf("history", "geography", "social") -> "📚"
            lowerTag == "definition" -> "📖"
            else -> "🏷️"
        }
    }

    // ============================================================================
    // QUICK REFERENCE ICON GROUPS
    // ============================================================================

    // Review mode icons
    object ReviewModeIcons {
        val Question = Icons.Filled.HelpOutline
        val Answer = Icons.Filled.CheckCircle
        val Reveal = Icons.Filled.Visibility
        val Correct = Icons.Filled.Check
        val Incorrect = Icons.Filled.Close
        val Next = Icons.Filled.NavigateNext
        val Previous = Icons.Filled.NavigateBefore
    }

    // Dialog action icons
    object DialogIcons {
        val Confirm = Icons.Filled.Check
        val Cancel = Icons.Filled.Close
        val Delete = Icons.Outlined.Delete
        val Edit = Icons.Outlined.Edit
    }

    // Statistics icons
    object StatsIcons {
        val TotalCards = Icons.Filled.AssignmentTurnedIn
        val Mastered = Icons.Filled.CheckCircle
        val Learning = Icons.Filled.School
        val Due = Icons.Filled.NotificationImportant
        val Accuracy = AutoStoriesIcon
        val Sessions = Icons.Filled.History
    }

    // ============================================================================
    // HELPER REFERENCE: Material Icon Categories
    // ============================================================================
    // Filled Icons (solid, commonly used):
    //   - Star, CheckCircle, Close, Delete, Search, Add, Refresh
    //   - Schedule, Warning, Info, Help, Settings
    //   - Edit, Copy, Share, Download
    //
    // Outlined Icons (stroked outline):
    //   - StarOutline, DeleteOutline, EditOutline
    //   - Used for secondary/inactive states
    //
    // For future custom SVG icons, reference them here:
    //   - painterResource(com.karyadina.common.generated.resources.Res.drawable.ic_hint_bulb)
    //   - painterResource(com.karyadina.common.generated.resources.Res.drawable.ic_formula)
    //   - painterResource(com.karyadina.common.generated.resources.Res.drawable.ic_flashcard)
    //   - painterResource(com.karyadina.common.generated.resources.Res.drawable.ic_celebration)
}
